import React, { useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import LoadingPage from 'pages/LoadingPage';

import FreelancerSignUp, {
  useFreelancerSignUp,
} from 'state/context/freelancer/FreelancerSignUp';

import FreelancerRoute from './components/freelancer-route';

import freelancerRoutes from './freelancer-routes';

type FreelancerSignUpRouteComponent = {
  component: React.LazyExoticComponent<React.FC<Record<string, never>>>;
};

const freelancerRouteComponents: Record<
  keyof typeof freelancerRoutes,
  FreelancerSignUpRouteComponent
> = {
  addCertificationDocument: {
    component: React.lazy(() => import('./AddCertificationDocument')),
  },
  addDiplomasDocument: {
    component: React.lazy(() => import('./AddDiplomasDocument')),
  },
  certificationInformation: {
    component: React.lazy(() => import('./CertificationInformation')),
  },
  competenciesInformation: {
    component: React.lazy(() => import('./CompetenciesInformation')),
  },
  diplomasInformation: {
    component: React.lazy(() => import('./DiplomasInformation')),
  },
  diplomasYearSelection: {
    component: React.lazy(() => import('./DiplomasYearSelection')),
  },
  documentsInformation: {
    component: React.lazy(() => import('./DocumentsInformation')),
  },
  fillInformation: {
    component: React.lazy(() => import('./FillInformation')),
  },
  forgotPassword: {
    component: React.lazy(() => import('./ForgotPassword')),
  },
  insuranceInformation: {
    component: React.lazy(() => import('./InsuranceInformation')),
  },
  kvkInformation: {
    component: React.lazy(() => import('./KVKInformation')),
  },
  landing: {
    component: React.lazy(() => import('./Landing')),
  },
  login: {
    component: React.lazy(() => import('./Login')),
  },
  personalInformation: {
    component: React.lazy(() => import('./PersonalInformation')),
  },
  personRegister: {
    component: React.lazy(() => import('./PersonRegisterInformation')),
  },
  resetPassword: {
    component: React.lazy(() => import('./ResetPassword')),
  },
  signUp: {
    component: React.lazy(() => import('./SignUp')),
  },
  submitApplication: {
    component: React.lazy(() => import('./SubmitApplication')),
  },
  welcome: {
    component: React.lazy(() => import('./Welcome')),
  },
};

const FreelancerSignUpRoutes = (): JSX.Element => {
  const navigate = useNavigate();
  const {
    signUpStatus,
    freelancerDetails,
    detailsIsSuccess,
    profileIsSuccess,
    statusIsSuccess,
  } = useFreelancerSignUp();

  useEffect(() => {
    if (detailsIsSuccess && profileIsSuccess && statusIsSuccess) {
      if (
        // redirect to Application Submitted page if "ready for verification" and not verified
        freelancerDetails?.ready_for_verification &&
        signUpStatus?.fillInformationComplete &&
        window.location.pathname !== freelancerRoutes.submitApplication.path
      ) {
        navigate(freelancerRoutes.submitApplication.path);
      }
    }
    if (
      // redirect to landing page if path == '/freelancer'
      [
        '/freelancer/',
        '/freelancer',
        // below is the path to old signup flow. instead, we want to redirect the user to the new signup flow.
        '/freelancer/signup/',
        '/freelancer/signup',
      ].includes(window.location.pathname)
    ) {
      navigate(freelancerRoutes.landing.path);
    }
  }, [
    navigate,
    detailsIsSuccess,
    freelancerDetails?.ready_for_verification,
    profileIsSuccess,
    signUpStatus?.fillInformationComplete,
    statusIsSuccess,
  ]);

  return (
    <Routes>
      {Object.entries(freelancerRoutes).map(([key, route]) => (
        <Route
          key={route.name}
          path={route.path}
          element={
            <FreelancerRoute requireAuth={route.requireAuth}>
              <React.Suspense fallback={<LoadingPage />}>
                {React.createElement(freelancerRouteComponents[key].component)}
              </React.Suspense>
            </FreelancerRoute>
          }
        />
      ))}
    </Routes>
  );
};

const FreelancerSignUpPage = (): JSX.Element => {
  return (
    <FreelancerSignUp.Provider>
      <FreelancerSignUpRoutes />
    </FreelancerSignUp.Provider>
  );
};

export default FreelancerSignUpPage;
